import { Injectable } from '@angular/core';
import {
  FeatureStatus,
  NaturalId,
} from '../population-menu/population.service';
import { MapHttpService } from '../../../../mapbox/services/map-http.service';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import {
  formatChartRangesAndHoverNumber,
  getPercents,
  getPieChartLayout,
  setAccessibleYears,
  withLoading,
} from '../shared/chart-utility';
import { Data, Layout } from 'plotly.js-dist-min';
import { convertArea } from '@turf/helpers';
import area from '@turf/area';
import { JsonResponse } from '../../../../../shared/api/backend-config';
import { of } from 'rxjs';
import { getFRCLabels } from '../../../../../shared/util/FrcDistribution';

export const HOUSEHOLDS_BY_VEHICLES = 'HOUSEHOLDS_BY_VEHICLES';
export const TIME_TO_DEPARTURE_TO_WORK = 'TIME_TO_DEPARTURE_TO_WORK';
export const TRAVEL_TIME_TO_WORK = 'TRAVEL_TIME_TO_WORK';
export const TRANSPORTATION_TO_WORK = 'TRANSPORTATION_TO_WORK';

export interface CommuteAndMobilityJSON {
  HOUSEHOLDS_BY_VEHICLES: HouseholdsByVehicles[];
  TIME_TO_DEPARTURE_TO_WORK: TimeOfDepartureToWork[];
  TRAVEL_TIME_TO_WORK: TravelTimeToWork[];
  TRANSPORTATION_TO_WORK: WorkCommuteModes[];
}

export interface HouseholdsByVehicles {
  year: number;
  vehiclesCount: number;
  households: number;
}

export interface WorkCommuteModes {
  year: number;
  travelType: string;
  workers: number;
}

export interface TravelTimeToWork {
  year: number;
  minutesFrom: number;
  minutesTo: number;
  workers: number;
}

export interface TimeOfDepartureToWork {
  year: number;
  timeInterval: string;
  workers: number;
}

export interface RoadLengthBackend {
  // length multiplied by lanes num
  roadLengthLanesWeighted: RoadLength;
  // summary length without considering lanes
  roadLength: RoadLength;
}

export interface RoadLength {
  motorway?: number;
  motorway_link?: number;
  primary?: number;
  primary_link?: number;
  trunk?: number;
  trunk_link?: number;
  residential?: number;
  secondary?: number;
  secondary_link?: number;
  service?: number;
  tertiary?: number;
  tertiary_link?: number;
}

export interface RoadDensityFeatureStatus extends FeatureStatus {
  containsRoadDensity: boolean;
}

export interface CommuteAndMobilityFeatureStatus extends FeatureStatus {
  containsHouseholdsByVehicles: boolean;
  householdsByVehiclesYear: number;
  containsWorkCommuteModes: boolean;
  workCommuteModesYear: number;
  containsTravelTimeToWork: boolean;
  travelTimeToWorkYear: number;
  containsTimeOfDepartureToWork: boolean;
  timeOfDepartureToWorkYear: number;
  containsRoadDensity: boolean;
  containsTrafficIntensity: boolean;
}

export interface TrafficIntensityBackend {
  distribution: RoadLength;
}

@Injectable({
  providedIn: 'root',
})
export class CommuteAndMobilityService {
  private householdsByVehiclesData: HouseholdsByVehicles[] = [];
  private timeOfDepartureToWorkData: TimeOfDepartureToWork[] = [];
  private travelTimeToWorkData: TravelTimeToWork[] = [];
  private workCommuteModesData: WorkCommuteModes[] = [];

  // Filled from the fetched data - the four datasets do not share a year range.
  public readonly householdsByVehiclesAccessibleYears: number[] = [];
  public readonly timeOfDepartureToWorkAccessibleYears: number[] = [];
  public readonly travelTimeToWorkAccessibleYears: number[] = [];
  public readonly workCommuteModesAccessibleYears: number[] = [];

  public featureStatus: CommuteAndMobilityFeatureStatus = {
    naturalId: '',
    isSelected: false,
    containsHouseholdsByVehicles: false,
    householdsByVehiclesYear: 0,
    containsTimeOfDepartureToWork: false,
    timeOfDepartureToWorkYear: 0,
    containsTravelTimeToWork: false,
    travelTimeToWorkYear: 0,
    containsWorkCommuteModes: false,
    workCommuteModesYear: 0,
    containsRoadDensity: false,
    containsTrafficIntensity: false,
  };

  public trafficIntensityChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public roadDensityChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly householdSizeByVehiclesChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly timeOfDepartureToWorkChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly travelTimeToWorkChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly workCommuteModesChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  constructor(private http: MapHttpService) {}

  // A row existing for a year doesn't mean the year has real numbers behind
  // it - some geography/year combinations (e.g. Florida at state level) come
  // back as all-zero rows. Years that sum to zero are dropped here so the
  // stepper never offers a year whose chart can only ever read "not enough
  // data" - accessibleYears becomes exactly the years worth stepping to.
  private static yearsWithData<T extends { year: number }>(
    rows: T[],
    value: (row: T) => number
  ): { year: number }[] {
    const totals = new Map<number, number>();

    for (const row of rows) {
      totals.set(row.year, (totals.get(row.year) ?? 0) + value(row));
    }

    return [...totals.entries()]
      .filter(([, total]) => total > 0)
      .map(([year]) => ({ year }));
  }

  public fillChartsData(feature: MapboxGeoJSONFeature): void {
    const id = feature.properties!.external_id;

    this.http
      .getDataForSingleCellUnified<CommuteAndMobilityJSON>(id, [
        HOUSEHOLDS_BY_VEHICLES,
        TIME_TO_DEPARTURE_TO_WORK,
        TRAVEL_TIME_TO_WORK,
        TRANSPORTATION_TO_WORK,
      ])
      .pipe(withLoading(this.featureStatus))
      .subscribe((res) => {
        const {
          HOUSEHOLDS_BY_VEHICLES,
          TIME_TO_DEPARTURE_TO_WORK,
          TRAVEL_TIME_TO_WORK,
          TRANSPORTATION_TO_WORK,
        } = res.object;

        this.featureStatus.naturalId = id;
        this.featureStatus.isSelected = true;

        if (HOUSEHOLDS_BY_VEHICLES?.length) {
          this.householdsByVehiclesData = HOUSEHOLDS_BY_VEHICLES;
          setAccessibleYears(
            this.householdsByVehiclesAccessibleYears,
            CommuteAndMobilityService.yearsWithData(
              HOUSEHOLDS_BY_VEHICLES,
              (d) => d.households
            )
          );

          if (this.householdsByVehiclesAccessibleYears.length) {
            this.featureStatus.containsHouseholdsByVehicles = true;
            this.featureStatus.householdsByVehiclesYear = Math.max(
              ...this.householdsByVehiclesAccessibleYears
            );
            this.updateHouseholdsByVehiclesChartByYear(
              this.featureStatus.householdsByVehiclesYear
            );
          } else {
            this.featureStatus.containsHouseholdsByVehicles = false;
          }
        } else {
          setAccessibleYears(this.householdsByVehiclesAccessibleYears, []);
          this.featureStatus.containsHouseholdsByVehicles = false;
        }

        if (TIME_TO_DEPARTURE_TO_WORK?.length) {
          this.timeOfDepartureToWorkData = TIME_TO_DEPARTURE_TO_WORK;
          setAccessibleYears(
            this.timeOfDepartureToWorkAccessibleYears,
            CommuteAndMobilityService.yearsWithData(
              TIME_TO_DEPARTURE_TO_WORK,
              (d) => d.workers
            )
          );

          if (this.timeOfDepartureToWorkAccessibleYears.length) {
            this.featureStatus.containsTimeOfDepartureToWork = true;
            this.featureStatus.timeOfDepartureToWorkYear = Math.max(
              ...this.timeOfDepartureToWorkAccessibleYears
            );
            this.updateTimeOfDepartureToWorkChartByYear(
              this.featureStatus.timeOfDepartureToWorkYear
            );
          } else {
            this.featureStatus.containsTimeOfDepartureToWork = false;
          }
        } else {
          setAccessibleYears(this.timeOfDepartureToWorkAccessibleYears, []);
          this.featureStatus.containsTimeOfDepartureToWork = false;
        }

        if (TRAVEL_TIME_TO_WORK?.length) {
          this.travelTimeToWorkData = TRAVEL_TIME_TO_WORK;
          setAccessibleYears(
            this.travelTimeToWorkAccessibleYears,
            CommuteAndMobilityService.yearsWithData(
              TRAVEL_TIME_TO_WORK,
              (d) => d.workers
            )
          );

          if (this.travelTimeToWorkAccessibleYears.length) {
            this.featureStatus.containsTravelTimeToWork = true;
            this.featureStatus.travelTimeToWorkYear = Math.max(
              ...this.travelTimeToWorkAccessibleYears
            );
            this.updateTravelTimeToWorkChartByYear(
              this.featureStatus.travelTimeToWorkYear
            );
          } else {
            this.featureStatus.containsTravelTimeToWork = false;
          }
        } else {
          setAccessibleYears(this.travelTimeToWorkAccessibleYears, []);
          this.featureStatus.containsTravelTimeToWork = false;
        }

        if (TRANSPORTATION_TO_WORK?.length) {
          this.workCommuteModesData = TRANSPORTATION_TO_WORK;
          setAccessibleYears(
            this.workCommuteModesAccessibleYears,
            CommuteAndMobilityService.yearsWithData(
              TRANSPORTATION_TO_WORK,
              (d) => d.workers
            )
          );

          if (this.workCommuteModesAccessibleYears.length) {
            this.featureStatus.containsWorkCommuteModes = true;
            this.featureStatus.workCommuteModesYear = Math.max(
              ...this.workCommuteModesAccessibleYears
            );
            this.updateWorkCommuteModesChartByYear(
              this.featureStatus.workCommuteModesYear
            );
          } else {
            this.featureStatus.containsWorkCommuteModes = false;
          }
        } else {
          setAccessibleYears(this.workCommuteModesAccessibleYears, []);
          this.featureStatus.containsWorkCommuteModes = false;
        }
      });

    this.fillRoadDensityChart(feature);
    this.fillTrafficIntensityChart(feature);
  }

  public updateHouseholdsByVehiclesChartByYear(year: number) {
    this.featureStatus.householdsByVehiclesYear = year;

    const filtered = this.householdsByVehiclesData
      .filter((d) => d.year === year)
      .sort((a, b) => a.vehiclesCount - b.vehiclesCount);

    this.fillHouseholdsByVehiclesChart(filtered);
  }

  public updateTimeOfDepartureToWorkChartByYear(year: number) {
    this.featureStatus.timeOfDepartureToWorkYear = year;

    const filtered = this.timeOfDepartureToWorkData
      .filter((d) => d.year === year)
      .sort((a, b) => a.workers - b.workers);

    this.fillTimeOfDepartureToWorkChart(filtered);
  }

  public updateTravelTimeToWorkChartByYear(year: number) {
    this.featureStatus.travelTimeToWorkYear = year;

    const filtered = this.travelTimeToWorkData
      .filter((d) => d.year === year)
      .sort((a, b) => a.minutesFrom - b.minutesFrom);

    this.fillTravelTimeToWorkChart(filtered);
  }

  public updateWorkCommuteModesChartByYear(year: number) {
    this.featureStatus.workCommuteModesYear = year;

    const filtered = this.workCommuteModesData
      .filter((d) => d.year === year)
      .sort((a, b) => a.workers - b.workers);

    this.fillWorkCommuteModesChart(filtered);
  }

  private fillHouseholdsByVehiclesChart(data: HouseholdsByVehicles[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsHouseholdsByVehicles = false;
      return;
    }

    const mappedData = data.map((el) => ({
      value: el.households,
      vehicles: el.vehiclesCount,
    }));

    const values = mappedData.map((el) => el.value);
    const keys = mappedData.map((el) => el.vehicles);
    const hoverTexts = values.map((value) =>
      formatChartRangesAndHoverNumber(value)
    );
    const total = values.reduce((acc, curr) => acc + curr, 0);

    if (values.length === 0 || total === 0) {
      this.featureStatus.containsHouseholdsByVehicles = false;
      return;
    }

    const percentages = getPercents(total, values);

    this.featureStatus.containsHouseholdsByVehicles = true;

    this.householdSizeByVehiclesChart.data = [
      {
        x: keys.map(String),
        y: percentages,
        type: 'bar',
        text: hoverTexts,
        textposition: 'none',
        hoverinfo: 'text',
        customdata: percentages.map(
          (value, i) =>
            `${keys[i]} Vehicles: ${value >= 1 ? value.toFixed(1) : '<1'}%`
        ),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: {
          color: keys.map((value) => {
            switch (value) {
              case 0:
                return '#AD95C6';
              case 1:
                return '#A084BD';
              case 2:
                return '#9272B3';
              case 3:
                return '#8561AA';
              case 4:
                return '#774FA0';
              default:
                return '#CCCCCC';
            }
          }),
        },
      },
    ] as Data;

    this.householdSizeByVehiclesChart.layout = {
      autosize: true,
      xaxis: {
        title: 'Number of Vehicles',
        type: 'category',
        tickfont: { size: 14 },
      },
      yaxis: {
        title: 'Households',
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      bargap: 0.5,
      font: {
        family: 'Manrope, sans-serif',
      },
      margin: { t: 35, b: 180, r: 30 },
    } as Layout;
  }

  private fillTimeOfDepartureToWorkChart(data: TimeOfDepartureToWork[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsTimeOfDepartureToWork = false;
      return;
    }

    const year = this.featureStatus.timeOfDepartureToWorkYear;
    const filtered = data.filter((d) => d.year === year);
    if (filtered.length === 0) {
      this.featureStatus.containsTimeOfDepartureToWork = false;
      return;
    }

    const parseTime = (timeStr: string): number => {
      const [_, hourStr, minuteStr, meridian] = timeStr.match(
        /(\d+):(\d+)\s*(AM|PM)/
      )!;
      let hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      if (meridian === 'PM' && hour !== 12) hour += 12;
      if (meridian === 'AM' && hour === 12) hour = 0;
      return hour * 60 + minute;
    };

    const formatTimeLabel = (timeStr: string): string => {
      const [_, hourStr, minuteStr, meridian] = timeStr.match(
        /(\d+):(\d+)\s*(AM|PM)/
      )!;
      const hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);
      if (minute === 0) {
        return `${hour}${meridian}`;
      } else {
        return `${hour}:${minute.toString().padStart(2, '0')}${meridian}`;
      }
    };

    const grouped = new Map<
      string,
      { totalWorkers: number; intervals: string[] }
    >();

    for (const d of filtered) {
      const from = d.timeInterval.split('–')[0].trim();
      const label = formatTimeLabel(from);

      if (!grouped.has(label)) {
        grouped.set(label, { totalWorkers: 0, intervals: [] });
      }

      const entry = grouped.get(label)!;
      entry.totalWorkers += d.workers;
      entry.intervals.push(d.timeInterval);
    }

    const sorted = Array.from(grouped.entries())
      .map(([label, val]) => {
        const minute = parseTime(val.intervals[0].split('–')[0].trim());
        return { label, ...val, minute };
      })
      .sort((a, b) => a.minute - b.minute);

    const yLabels = sorted.map((d) => d.label);
    const workers = sorted.map((d) => d.totalWorkers);
    const intervals = sorted.map((d) => d.intervals.join(', '));
    const total = workers.reduce((a, b) => a + b, 0);

    if (total === 0) {
      this.featureStatus.containsTimeOfDepartureToWork = false;
      return;
    }

    const percentages = getPercents(total, workers);

    this.featureStatus.containsTimeOfDepartureToWork = true;

    this.timeOfDepartureToWorkChart.data = [
      {
        x: percentages,
        y: yLabels,
        type: 'bar',
        orientation: 'h',
        hoverinfo: 'text',
        customdata: percentages.map(
          (p, i) => `${intervals[i]}: ${p >= 1 ? p.toFixed(1) : '<1'}%`
        ),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: { color: '#02A4FF' },
      },
    ] as Data;

    this.timeOfDepartureToWorkChart.layout = {
      autosize: true,
      xaxis: {
        title: {
          text: 'Workers, %',
          font: { size: 14 },
        } as any,
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      yaxis: {
        title: '',
        type: 'category',
        showgrid: true,
      },
      margin: { t: 35, b: 180, r: 30, pad: 5 },
      bargap: 0.5,
      font: {
        family: 'Manrope, sans-serif',
      },
      hovermode: 'closest',
    } as Layout;
  }

  private fillTravelTimeToWorkChart(data: TravelTimeToWork[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsTravelTimeToWork = false;
      return;
    }

    const latestYear = this.featureStatus.travelTimeToWorkYear;
    const filtered = data
      .filter((d) => d.year === latestYear)
      .sort((a, b) => a.minutesFrom - b.minutesFrom);

    if (filtered.length === 0) {
      this.featureStatus.containsTravelTimeToWork = false;
      return;
    }

    const keys = filtered.map((el) => {
      if (el.minutesFrom === 90 && el.minutesTo === 0) return '90+';
      return `${el.minutesFrom}-${el.minutesTo}`;
    });

    const values = filtered.map((el) => el.workers);
    const total = values.reduce((acc, curr) => acc + curr, 0);

    if (values.length === 0 || total === 0) {
      this.featureStatus.containsTravelTimeToWork = false;
      return;
    }

    const percentages = getPercents(total, values);
    const hoverTexts = values.map(
      (value, i) => `${keys[i]} min: ${formatChartRangesAndHoverNumber(value)}`
    );

    this.featureStatus.containsTravelTimeToWork = true;

    this.travelTimeToWorkChart.data = [
      {
        y: keys,
        x: percentages,
        type: 'bar',
        orientation: 'h',
        text: hoverTexts,
        textposition: 'none',
        hoverinfo: 'text',
        customdata: percentages.map(
          (value, i) =>
            `${keys[i]} min: ${value >= 1 ? value.toFixed(1) : '<1'}%`
        ),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: {
          color: '#774FA0',
        },
      },
    ] as Data;

    this.travelTimeToWorkChart.layout = {
      autosize: true,
      xaxis: {
        title: {
          text: 'Workers, %',
          font: { size: 14 },
        } as any,
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      yaxis: {
        title: 'Travel time to work, min',
      },
      margin: { t: 35, b: 180, r: 30, pad: 5 },
      bargap: 0.5,
      font: {
        family: 'Manrope, sans-serif',
      },
    } as Layout;
  }

  private fillWorkCommuteModesChart(data: WorkCommuteModes[]): void {
    if (!data || data.length === 0) {
      this.featureStatus.containsWorkCommuteModes = false;
      return;
    }

    const latestYear = this.featureStatus.workCommuteModesYear;

    const filtered = data.filter((d) => d.year === latestYear);

    if (filtered.length === 0) {
      this.featureStatus.containsWorkCommuteModes = false;
      return;
    }

    const mappedData = filtered
      .map((el) => ({
        value: Math.round(el.workers),
        travelType: this.mapTravelTypeReadable(el.travelType),
      }))
      .filter((el) => el.value > 0);

    const values = mappedData.map((el) => el.value);
    const labels = mappedData.map((el) => el.travelType);

    const total = values.reduce((acc, curr) => acc + curr, 0);

    if (total === 0) {
      this.featureStatus.containsWorkCommuteModes = false;
      return;
    }

    this.featureStatus.containsWorkCommuteModes = true;

    this.workCommuteModesChart.data = [
      {
        values: values,
        labels: labels,
        type: 'pie',
        textposition: 'inside',
        hoverinfo: 'label+percent+value',
        hovertemplate:
          '&nbsp;&nbsp;%{label}: %{value} workers (%{percent})&nbsp;&nbsp;<extra></extra>',
        marker: {
          colors: labels.map((label) => this.getTravelTypeColor(label)),
        },
      },
    ] as Data;

    this.workCommuteModesChart.layout = getPieChartLayout(
      undefined,
      'Travel type',
      'Workers'
    );
  }

  private mapTravelTypeReadable(travelType: string): string {
    switch (travelType) {
      case 'CAR':
        return 'Car';
      case 'PUBLIC':
      case 'PUBLIC_TRANSPORTATION':
        return 'Public transport';
      case 'TAXICAB':
        return 'Taxi';
      case 'MOTORCYCLE':
        return 'Motorcycle';
      case 'BICYCLE':
        return 'Bicycle';
      case 'WALKED':
        return 'Walked';
      case 'OTHER':
      case 'OTHER_MEANS':
        return 'Other';
      case 'WORKED_AT_HOME':
        return 'Worked at home';
      default:
        return 'Other';
    }
  }

  private getTravelTypeColor(travelType: string): string {
    switch (travelType) {
      case 'Car':
        return '#336DB1';
      case 'Public transport':
        return '#F38F20';
      case 'Taxi':
        return '#47A34B';
      case 'Motorcycle':
        return '#D94134';
      case 'Bicycle':
        return '#975BA5';
      case 'Walked':
        return '#855B4A';
      case 'Other':
        return '#D49AAE';
      case 'Worked at home':
        return '#AFAFAF';
      default:
        return '#CCCCCC';
    }
  }

  private fillRoadDensityChart(feature: MapboxGeoJSONFeature): void {
    const areaMi2: number = convertArea(area(feature), 'meters', 'miles');
    const featureId: NaturalId = feature.properties!.external_id;

    this.http
      .getRoadDensityData(featureId)
      .pipe(withLoading(this.featureStatus))
      .subscribe(
        (data: JsonResponse<RoadLengthBackend>) => {
          if (data.object.roadLengthLanesWeighted) {
            this.featureStatus.naturalId = featureId;
            this.featureStatus.isSelected = true;
          }

          const maxValue = Math.max(
            ...Object.values(data.object.roadLengthLanesWeighted)
          );

          if (!maxValue || maxValue === -Infinity) {
            this.featureStatus.naturalId = featureId;
            this.featureStatus.containsRoadDensity = false;
          } else {
            this.featureStatus.naturalId = featureId;
            this.featureStatus.containsRoadDensity = true;
          }

          const groupedByFRC = CommuteAndMobilityService.getRoadsGroupedByFRC(
            data.object.roadLengthLanesWeighted
          );

          const values = Object.values(groupedByFRC).map((el) =>
            this.getRoadDensityPerMi2(el, areaMi2).toFixed(2)
          );
          const labels = Object.keys(groupedByFRC).map(
            (el) => el.charAt(0).toUpperCase() + el.slice(1)
          );

          this.roadDensityChart.data = [
            {
              values: values,
              labels: labels,
              type: 'pie',
              textposition: 'inside',
              hoverinfo: 'label+percent+value',
              hovertemplate:
                '&nbsp;&nbsp;%{label}: %{value} mi per mi² (%{percent})&nbsp;&nbsp;<extra></extra>',
            },
          ] as Data;

          this.roadDensityChart.layout = getPieChartLayout(
            undefined,
            'Road type',
            'mi per mi²'
          );
        },
        (error) => {
          this.featureStatus.isSelected = true;
          this.featureStatus.containsRoadDensity = false;
          console.error('ERROR FETCHING ROAD DENSITY', error);
          return of(null);
        }
      );
  }

  public static getRoadsGroupedByFRC(roadLengthData: RoadLength): {
    [key: string]: number;
  } {
    const roadLengthByFrc: { [key: string]: number } = {};

    for (let [frc, length] of Object.entries(roadLengthData)) {
      const frcKey = `FRC ${getFRCLabels(frc)}`;

      if (roadLengthByFrc[frcKey]) {
        roadLengthByFrc[frcKey] += length;
      } else {
        roadLengthByFrc[frcKey] = length;
      }
    }

    return roadLengthByFrc;
  }

  private getRoadDensityPerMi2(
    roadLength: number,
    featureAreaMi2: number
  ): number {
    const metersInMile = 1609.344;
    const roadLengthMi = roadLength / metersInMile;
    const roadDensityPerMi2 = roadLengthMi / featureAreaMi2;

    return roadDensityPerMi2;
  }

  private fillTrafficIntensityChart(feature: MapboxGeoJSONFeature): void {
    const id = feature.properties?.external_id;

    this.http
      .getTrafficIntensityChartData(id)
      .pipe(withLoading(this.featureStatus))
      .subscribe(
        (data) => {
          if (data.object.distribution) {
            this.featureStatus.naturalId = id;
            this.featureStatus.isSelected = true;
            this.featureStatus.containsTrafficIntensity = true;
          }

          const groupedByFRC = CommuteAndMobilityService.getRoadsGroupedByFRC(
            data.object.distribution
          );

          const sorted = Object.entries(groupedByFRC)
            .map(([label, value]) => ({
              label: label.charAt(0).toUpperCase() + label.slice(1),
              value: Math.round(value),
            }))
            .sort((a, b) => a.value - b.value);

          const maxValue = Math.max(...sorted.map((el) => el.value));

          if (!maxValue || maxValue === -Infinity) {
            this.featureStatus.naturalId = id;
            this.featureStatus.containsTrafficIntensity = false;
          } else {
            this.featureStatus.naturalId = id;
            this.featureStatus.containsTrafficIntensity = true;
          }

          // one trace per FRC class so each bar is independently toggleable
          // by clicking its entry in the legend (native Plotly behavior)
          this.trafficIntensityChart.data = sorted.map((el) => ({
            y: [el.label],
            x: [el.value],
            type: 'bar',
            orientation: 'h',
            name: el.label,
            hoverinfo: 'text',
            customdata: [
              `${el.label} AADT: ${formatChartRangesAndHoverNumber(el.value)}`,
            ],
            hovertemplate: `%{customdata}<extra></extra>`,
            marker: { color: '#02A4FF' },
          })) as Data;

          this.trafficIntensityChart.layout = {
            autosize: true,
            // fixed height: with autosize alone Plotly can mismeasure the
            // container on mount and render taller than the allotted space
            height: 330,
            xaxis: {
              title: {
                text: 'AADT, veh/day',
                font: { size: 14 },
              } as any,
              tickformat: ',.0f',
            },
            yaxis: {
              title: '',
              type: 'category',
              showgrid: true,
            },
            margin: { t: 35, b: 40, l: 50, r: 65, pad: 5 },
            bargap: 0.3,
            font: {
              family: 'Manrope, sans-serif',
            },
            showlegend: true,
            legend: {
              orientation: 'v',
              traceorder: 'reversed',
              x: 1,
              xanchor: 'left',
              y: 1,
              font: { size: 10 },
            } as any,
            hovermode: 'closest',
          } as Layout;
        },
        (error) => {
          this.featureStatus.isSelected = true;
          this.featureStatus.containsTrafficIntensity = false;
          console.error('ERROR FETCHING TRAFFIC INTENSITY', error);
          return of(null);
        }
      );
  }
}
